// The floor this person is looking at (qa-runs#1160; design/32 §5): the DM picks any of the scene's floors, a player one
// of the floors their tokens are on (the only ones they were sent). Outside React like `selection`, so the canvas reads
// it too. A scene with one floor has no viewed floor at all: nothing is filtered, and it plays exactly as before.
import { floorBase, floorOf, groundFloor, hasFloors, sceneFloors, type Floor } from '@dragons-table/vtt-shared';

type Listener = (id: string | null) => void;
let picked: string | null = null;
const listeners = new Set<Listener>();

let marker: ((at: { i: number; j: number }) => void) | null = null;
export const floorView = {
  get: (): string | null => picked,
  set(id: string | null): void { if (id === picked) return; picked = id; for (const l of listeners) l(picked); },
  subscribe(l: Listener): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
  /** Openings (slice B): while the DM marks them, a press on the board toggles that square of the viewed floor. */
  marking: (): boolean => marker !== null,
  mark(at: { i: number; j: number }): void { marker?.(at); },
  setMarker(m: ((at: { i: number; j: number }) => void) | null): void { marker = m; for (const l of listeners) l(picked); },
};

/** What a player's last move reached (qa-runs#1240): the floors joined at that square, as the server said. */
export interface FloorOffer { id: string; floors: Array<{ id: string; name: string }> }
let offer: FloorOffer | null = null;
const offerListeners = new Set<(o: FloorOffer | null) => void>();
export const floorOffer = {
  get: (): FloorOffer | null => offer,
  set(o: FloorOffer | null): void { if (o === offer) return; offer = o; for (const l of offerListeners) l(offer); },
  subscribe(l: (o: FloorOffer | null) => void): () => void { offerListeners.add(l); return () => { offerListeners.delete(l); }; },
};

interface SceneLike { scene?: { floors?: Floor[] | undefined } | undefined; objects: Record<string, { floor?: string | undefined; owners?: readonly string[]; mine?: boolean }> }

/**
 * The floor in view: the picked one while the scene has it; else the DM's ground floor, or a player's first token's
 * floor (the floors they hold are only theirs). Null with one floor.
 */
export function viewedFloor(state: SceneLike, meId: string, isDm: boolean, pick: string | null = picked): string | null {
  if (!hasFloors(state.scene)) return null;
  const floors = sceneFloors(state.scene);
  if (pick && floors.some((f) => f.id === pick)) return pick;
  if (!isDm) {
    const mine = Object.values(state.objects).find((o) => o.mine || o.owners?.includes(meId));
    if (mine) return floorOf(mine, floors);
  }
  return groundFloor(floors);
}

/** The things on the floor in view (all of them with one floor). */
export function onFloor<T extends { floor?: string | undefined }>(objects: Record<string, T>, floors: readonly Floor[] | undefined, floor: string | null): Record<string, T> {
  if (!floor || !floors || floors.length < 2) return objects;
  const out: Record<string, T> = {};
  for (const [id, o] of Object.entries(objects)) if (floorOf(o, floors) === floor) out[id] = o;
  return out;
}

/** Where the floor in view starts, in squares: what a new token, area or wall placed there stands on. */
export const viewedBase = (floors: readonly Floor[] | undefined, floor: string | null): number => (floor && floors && floors.length > 1 ? floorBase(floors, floor) : 0);

const TIP_KEY = 'vtt.heightTip';
/** The person's switch for the height tooltip (design/32 §5): on unless they turned it off, in this browser alone. */
export function heightTipOn(): boolean {
  try { return localStorage.getItem(TIP_KEY) !== 'off'; } catch { return true; }
}
export function setHeightTip(on: boolean): void {
  try { if (on) localStorage.removeItem(TIP_KEY); else localStorage.setItem(TIP_KEY, 'off'); } catch { /* private window: on for this visit */ }
  for (const l of tipListeners) l(on);
}
const tipListeners = new Set<(on: boolean) => void>();
export const onHeightTip = (l: (on: boolean) => void): (() => void) => { tipListeners.add(l); return () => { tipListeners.delete(l); }; };
